import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyOrdersAPI, OrderUnifiedStatus, OrdersQueryAPI } from '@hero/api';
import type { DateType, Order } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useMyOrdersListApi, useOrderExportApi } from '@hero/api-hooks';
import { useOrderQueryStore } from '@hero/hooks-shared';
import type { OrderTableColumn, UseOrderAgainConfig } from '@hero/hooks-shared';
import { LoadMainIcon, OrderStatusDynamicIcon } from '@hero/web-icons';
import { getOrderStatusBadgeClass } from '@hero/web-theme';

import { OrderTable } from './OrderTable';
import { OrderBulkActions } from './OrderBulkActions/OrderBulkActions';
import { TableEmptyState } from '../../../app/Table/TableEmptyState';

export interface OrderTableManagerProps {
  /** Whose perspective the table is rendered for. */
  displayForUser: 'customer' | 'carrier';
  /**
   * Extra query params AND-merged into every list request. The
   * canonical use-case is `{ clientId }` to scope the table to one
   * CRM client; passing other tenant-mutable fields (`carrierId`,
   * `customerId`, `driverId`) is a no-op because the backend strips
   * them on `getMyOrders`.
   */
  scopeQuery?: Partial<MyOrdersAPI['req']>;
  /**
   * `true` (default) on `/my-orders` — filters live in the global
   * `useOrderQueryStore` so the UI survives a remount and shares
   * state with anything else hanging off the same store. `false` for
   * embedded tables (e.g. CRM client tab) where filter bleed between
   * surfaces is undesirable; the manager keeps filters in local
   * state instead.
   */
  useGlobalFilterStore?: boolean;
  /** Render bulk-actions bar + selection checkboxes. Defaults to `true`. */
  bulkActions?: boolean;
  /** Override the default column set. */
  columns?: OrderTableColumn[];
  /**
   * Optional inline-update callback (POD ref edit, etc.). When
   * omitted, the corresponding cell stays read-only. Apps wire this
   * to their own carrier-/customer-SDK PATCH wrapper because the
   * mutation hook depends on the role-scoped `useApi`.
   */
  updateOrderFn?: (data: { _id: string; pod: { podRef?: string } }) => Promise<Order>;
  /** Optional empty-state node. Falls back to a generic "no orders". */
  emptyState?: ReactNode;
  /** Forwards order-again wiring to the drawer. */
  drawerProps?: { orderAgainConfig?: UseOrderAgainConfig };
}

/**
 * Page-host wrapper around `OrderTable` + `OrderBulkActions`. The
 * `MyOrdersPage` and the CRM client workspace's Orders tab were
 * previously two different bodies of code (a full filter+table
 * setup vs. a `SharedList + OrderCard` list). This manager makes the
 * table the canonical orders surface in any host that needs it,
 * with two knobs the host actually has to think about — `scopeQuery`
 * (e.g. `{ clientId }`) and `useGlobalFilterStore`.
 *
 * The manager owns nothing the host should care about: data fetch,
 * filter state, bulk-selection, sort. The host is still responsible
 * for mounting the drawers row clicks pop (`OrderDrawer`,
 * `DriverDrawer`, `VehicleDrawer`) — those are layout concerns and
 * shouldn't be duplicated per surface.
 */
export const OrderTableManager: React.FC<OrderTableManagerProps> = ({
  displayForUser,
  scopeQuery,
  useGlobalFilterStore = true,
  bulkActions = true,
  columns,
  updateOrderFn,
  emptyState,
  drawerProps,
}) => {
  const { t } = useTranslation('m-order');

  // Filter source — global store on /my-orders, local state when
  // embedded so a CRM client tab's filters don't leak into the main
  // orders page or vice versa.
  const globalFilters = useOrderQueryStore(s => s.filters);
  const globalSetFilter = useOrderQueryStore(s => s.setFilter);
  const globalSearch = useOrderQueryStore(s => s.searchTerm);
  const globalSetSearch = useOrderQueryStore(s => s.setSearch);

  const [localFilters, setLocalFilters] = useState<OrdersQueryAPI>({});
  const [localSearch, setLocalSearch] = useState('');

  const filters = useGlobalFilterStore ? globalFilters : localFilters;
  const setFilter = useGlobalFilterStore
    ? globalSetFilter
    : (q: OrdersQueryAPI) => setLocalFilters(prev => ({ ...prev, ...q }));
  const searchTerm = useGlobalFilterStore ? globalSearch : localSearch;
  const setSearch = useGlobalFilterStore
    ? globalSetSearch
    : (term: string) => {
        setLocalSearch(term ?? '');
        setLocalFilters(prev => ({ ...prev, search: term || undefined }));
      };

  const mergedQuery = useMemo(
    () => ({ ...filters, ...scopeQuery }) as MyOrdersAPI['req'],
    [filters, scopeQuery],
  );

  const {
    data: orders,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
  } = useMyOrdersListApi(mergedQuery);

  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

  // Reset selection on scope change so we don't carry IDs from a
  // different client/scope into the bulk-actions bar.
  useEffect(() => {
    setSelectedRowIds(new Set());
  }, [scopeQuery]);

  const statusOptions = useMemo(
    () => [
      {
        value: 'any_active_status' as OrderUnifiedStatus,
        label: t('table.status.active'),
        icon: <OrderStatusDynamicIcon status="any_active_status" size="small" />,
      },
      {
        value: 'delivered' as OrderUnifiedStatus,
        label: t('table.status.delivered'),
        icon: <OrderStatusDynamicIcon status="delivered" size="small" />,
        className: getOrderStatusBadgeClass('delivered'),
      },
      {
        value: 'canceled' as OrderUnifiedStatus,
        label: t('table.status.canceled'),
        icon: <OrderStatusDynamicIcon status="canceled" size="small" />,
        className: getOrderStatusBadgeClass('canceled'),
      },
    ],
    [t],
  );

  const tableFilters = useMemo(
    () => ({
      showSearch: true,
      statusFilter: {
        value: filters.unifiedStatus,
        onChange: (value: OrderUnifiedStatus | undefined) =>
          setFilter({ unifiedStatus: value }),
        options: statusOptions,
      },
      dateFilter: {
        value: filters.pickupSimpleDate,
        onChange: (value: string | undefined) =>
          setFilter({
            pickupSimpleDate: value as DateType | undefined,
            pickupDate: undefined,
            pickupDateBeforeOrEqual: undefined,
          }),
      },
    }),
    [filters.unifiedStatus, filters.pickupSimpleDate, setFilter, statusOptions],
  );

  const selectedOrders = (orders ?? []).filter(o => selectedRowIds.has(o._id.toString()));

  const { download: exportOrders, isPending: isExporting } = useOrderExportApi({
    filename: req => `orders-${req.orderIds?.length ?? 'export'}.xlsx`,
  });

  const handleExportSelected = useCallback(
    async (orderIds: string[]) => {
      await exportOrders({
        orderIds: orderIds.map(id => getObjectId(id)),
      });
      setSelectedRowIds(new Set());
    },
    [exportOrders],
  );

  const fallbackEmpty = (
    <TableEmptyState
      icon={<LoadMainIcon className="h-7 w-7" />}
      title={t('m-order:table.empty_carrier.title')}
      description={t('m-order:table.empty_carrier.description')}
    />
  );

  return (
    <>
      {bulkActions && selectedRowIds.size > 0 && (
        <OrderBulkActions
          selectedOrderIds={Array.from(selectedRowIds)}
          orders={selectedOrders}
          onExport={handleExportSelected}
          isExporting={isExporting}
          onCancel={() => setSelectedRowIds(new Set())}
        />
      )}

      <div className={bulkActions && selectedRowIds.size > 0 ? 'pb-20' : ''}>
        <OrderTable
          orders={orders ?? []}
          isLoading={isLoading}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          searchProps={{ term: searchTerm, setTerm: setSearch }}
          onSort={handleSort}
          displayForUser={displayForUser}
          hasDetailDrawer
          updateOrderFn={updateOrderFn}
          enableRowSelection={bulkActions}
          selectedRowIds={selectedRowIds}
          onSelectionChange={setSelectedRowIds}
          columns={columns}
          filters={tableFilters}
          emptyState={emptyState ?? fallbackEmpty}
          hasActiveFilters={
            Boolean(searchTerm) ||
            Boolean(filters.unifiedStatus) ||
            Boolean(filters.pickupSimpleDate)
          }
          onLoadMore={onLoadMore}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          drawerProps={drawerProps}
        />
      </div>
    </>
  );
};
